
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Beaker, BarChart, Users, Search, Settings } from 'lucide-react';
import { useOrgPaths } from '@/hooks/useOrgPaths';

interface SidebarProps {
  isOpen: boolean;
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const sidebarRef = useRef<HTMLDivElement>(null);
  const paths = useOrgPaths();

  // Handle clicks outside the sidebar to close it on mobile
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && 
          sidebarRef.current && 
          !sidebarRef.current.contains(event.target as Node)) {
        onClose?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  return (
    <>
      {/* Mobile overlay backdrop */}
      {isOpen && (
        <div
          className="md:hidden fixed inset-0 z-30 bg-black/50 transition-opacity"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        ref={sidebarRef}
        className={`
          fixed top-0 left-0 z-40 h-screen pt-14
          transition-transform duration-300 ease-in-out bg-background border-r border-border
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          w-64 md:translate-x-0
        `}
      >
        <div className="h-full px-3 pb-4 overflow-y-auto">
          <ul className="space-y-2 font-medium">
            <li>
              <Link
                to={paths.home()}
                className="flex items-center p-2 text-foreground rounded-lg hover:bg-muted"
                onClick={() => onClose?.()}
              >
                <Beaker className="w-5 h-5 text-agri-green transition duration-75" />
                <span className="ml-3">Trials</span>
              </Link>
            </li>
            <li>
              <Link
                to={paths.section('traits')}
                className="flex items-center p-2 text-foreground rounded-lg hover:bg-muted"
                onClick={() => onClose?.()}
              >
                <BarChart className="w-5 h-5 text-agri-green transition duration-75" />
                <span className="ml-3">Traits</span>
              </Link>
            </li>
            {/* TODO: Uncomment when Scouting feature is implemented
            <li>
              <Link
                to={paths.section('scouting')}
                className="flex items-center p-2 text-foreground rounded-lg hover:bg-muted"
                onClick={() => onClose?.()}
              >
                <Search className="w-5 h-5 text-agri-green transition duration-75" />
                <span className="ml-3">Scouting</span>
              </Link>
            </li>
            */}
            <li>
              <Link
                to={paths.section('team')}
                className="flex items-center p-2 text-foreground rounded-lg hover:bg-muted"
                onClick={() => onClose?.()}
              >
                <Users className="w-5 h-5 text-agri-green transition duration-75" />
                <span className="ml-3">Team</span>
              </Link>
            </li>
            <li>
              <Link
                to={paths.section('settings')}
                className="flex items-center p-2 text-foreground rounded-lg hover:bg-muted"
                onClick={() => onClose?.()}
              >
                <Settings className="w-5 h-5 text-agri-green transition duration-75" />
                <span className="ml-3">Settings</span>
              </Link>
            </li>
          </ul>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
